import { useEffect, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useThemeStore } from '../store/theme-store';
import { useFilterStore } from '@/features/filters/store/filter-store';

export type Theme = 'light' | 'dark';

const darkQuery = '(prefers-color-scheme: dark)';
const subscribe = (onChange: () => void) => {
  const mq = window.matchMedia(darkQuery);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
};
const getSystemDark = () => window.matchMedia(darkQuery).matches;

/**
 * Resolved theme. Priority: ?theme=light|dark (inbound-only) > embeds
 * (controls=false) stay light so partner pages don't flip > user choice >
 * OS preference.
 */
export function useTheme(): Theme {
  const [searchParams] = useSearchParams();
  const preference = useThemeStore((s) => s.preference);
  const controlsEnabled = useFilterStore((s) => s.controlsEnabled);
  const systemDark = useSyncExternalStore(subscribe, getSystemDark);

  const param = searchParams.get('theme');
  if (param === 'light' || param === 'dark') return param;
  if (!controlsEnabled) return 'light';
  if (preference !== 'system') return preference;
  return systemDark ? 'dark' : 'light';
}

/** Mirrors the resolved theme onto <html class="dark"> for Tailwind. */
export function useApplyTheme() {
  const theme = useTheme();
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.documentElement.style.colorScheme = theme;
  }, [theme]);
  return theme;
}
